import { useState } from 'react';
import { useAnimationStore, createNewPreset } from '../stores/animationStore';
import { AnimationCategory, AnimationSubType } from '../types';
import {
  BgColorsOutlined,
  BorderOutlined,
  FontSizeOutlined,
  LoadingOutlined,
  AppstoreOutlined,
  SwapOutlined,
} from '@ant-design/icons';

type CategoryTab = AnimationCategory;

const categoryTabs: { id: CategoryTab; label: string; icon: React.ReactNode }[] = [
  { id: 'backgrounds', label: 'Фоны', icon: <BgColorsOutlined /> },
  { id: 'buttons', label: 'Кнопки', icon: <BorderOutlined /> },
  { id: 'text', label: 'Текст', icon: <FontSizeOutlined /> },
  { id: 'loaders', label: 'Лоадеры', icon: <LoadingOutlined /> },
  { id: 'cards', label: 'Карточки', icon: <AppstoreOutlined /> },
];

// Templates for each category
const categoryTemplates: Record<
  CategoryTab,
  { name: string; subType: AnimationSubType; inDevelopment?: boolean }[]
> = {
  backgrounds: [
    { name: 'Снег', subType: 'snow' },
    { name: 'Дождь', subType: 'rain' },
    { name: 'Звёзды', subType: 'stars' },
    { name: 'Огонь', subType: 'fire' },
    { name: 'Пузыри', subType: 'bubbles' },
    { name: 'Конфетти', subType: 'confetti' },
    { name: 'Градиент', subType: 'gradient', inDevelopment: true },
    { name: 'Волны', subType: 'waves', inDevelopment: true },
    { name: 'Сетка', subType: 'grid', inDevelopment: true },
    { name: 'Аврора', subType: 'aurora', inDevelopment: true },
  ],
  buttons: [
    { name: 'Firefly', subType: 'firefly' },
    { name: 'Border Glow', subType: 'border-glow' },
    { name: 'Border Animate', subType: 'border-animate', inDevelopment: true },
    { name: 'Fill', subType: 'fill', inDevelopment: true },
    { name: 'Scale', subType: 'scale', inDevelopment: true },
    { name: 'Shimmer', subType: 'shimmer', inDevelopment: true },
    { name: 'Glitch', subType: 'glitch', inDevelopment: true },
  ],
  text: [
    { name: 'Печать', subType: 'typewriter' },
    { name: 'Fade', subType: 'fade' },
    { name: 'Wave', subType: 'wave' },
    { name: 'Gradient', subType: 'gradient' },
    { name: 'Glitch', subType: 'glitch' },
    { name: 'Typing', subType: 'typing' },
  ],
  loaders: [
    { name: 'Spinner', subType: 'spinner' },
    { name: 'Pulse', subType: 'pulse' },
    { name: 'Dots', subType: 'dots' },
    { name: 'Progress', subType: 'progress' },
    { name: 'Ring', subType: 'ring' },
    { name: 'Bounce', subType: 'bounce' },
  ],
  cards: [
    { name: 'Flip', subType: 'flip', inDevelopment: true },
    { name: 'Slide', subType: 'slide', inDevelopment: true },
    { name: 'Fade', subType: 'fade', inDevelopment: true },
    { name: 'Zoom', subType: 'zoom', inDevelopment: true },
    { name: 'Rotate', subType: 'rotate', inDevelopment: true },
  ],
};

export const PresetLibrary = () => {
  const {
    presets,
    currentPreset,
    setCurrentPreset,
    addPreset,
    activeCategoryTab,
    setActiveCategoryTab,
  } = useAnimationStore();

  const handleCreatePreset = (template: {
    name: string;
    subType: AnimationSubType;
    inDevelopment?: boolean;
  }) => {
    if (template.inDevelopment) return;
    const newPreset = createNewPreset(template.name, activeCategoryTab, template.subType);
    setCurrentPreset(newPreset);
  };

  const handleLoadPreset = (preset: (typeof presets)[0]) => {
    setCurrentPreset(preset);
  };

  const templates = categoryTemplates[activeCategoryTab] || [];

  return (
    <div
      style={{
        background: '#1a1a1a',
        borderRadius: 12,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Category Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        {categoryTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveCategoryTab(tab.id)}
            style={{
              flex: '1 1 calc(33% - 4px)',
              minWidth: 70,
              background: activeCategoryTab === tab.id ? '#3498db' : '#2a2a2a',
              border: '1px solid #333',
              borderRadius: 6,
              padding: '8px 4px',
              color: '#fff',
              fontSize: 11,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Templates for current category */}
      <div>
        <h3 style={{ color: '#fff', marginBottom: 12, fontSize: 14 }}>Шаблоны</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
          }}
        >
          {templates.map((template, i) => (
            <button
              key={i}
              onClick={() => handleCreatePreset(template)}
              disabled={template.inDevelopment}
              style={{
                background: template.inDevelopment ? '#1a1a1a' : '#2a2a2a',
                border:
                  currentPreset?.subType === template.subType
                    ? '2px solid #3498db'
                    : '1px solid #333',
                borderRadius: 8,
                padding: '16px 8px',
                color: template.inDevelopment ? '#555' : '#fff',
                fontSize: 12,
                cursor: template.inDevelopment ? 'not-allowed' : 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                minHeight: 60,
                aspectRatio: '1',
              }}
            >
              <span style={{ fontWeight: 500 }}>{template.name}</span>
              {template.inDevelopment && (
                <span
                  style={{
                    fontSize: 9,
                    color: '#e74c3c',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                  }}
                >
                  WIP
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Saved presets */}
      {presets.length > 0 && (
        <div>
          <h3 style={{ color: '#fff', marginBottom: 12, fontSize: 14 }}>
            Сохранённые ({presets.length})
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
            }}
          >
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handleLoadPreset(preset)}
                style={{
                  background: currentPreset?.id === preset.id ? '#2a3a4a' : '#2a2a2a',
                  border: currentPreset?.id === preset.id ? '2px solid #3498db' : '1px solid #333',
                  borderRadius: 8,
                  padding: '12px 8px',
                  color: '#fff',
                  fontSize: 11,
                  cursor: 'pointer',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  minHeight: 50,
                }}
              >
                <span style={{ fontWeight: 500 }}>{preset.name}</span>
                <span style={{ color: '#888', fontSize: 9 }}>{preset.category}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
